import React, { useRef, useEffect } from "react";

/* Eén keer globale stijl voor alle box-sliders (voorkomt dat alleen eerste werkt) */
export const BOX_SLIDER_GLOBAL_STYLE = `
  input[type="range"].box-slider-input {
    -webkit-appearance: none;
    appearance: none;
  }
  input[type="range"].box-slider-input::-webkit-slider-runnable-track {
    width: 100%;
    height: 100%;
    background: transparent;
  }
  input[type="range"].box-slider-input::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 4px;
    height: 40px;
    border-radius: 2px;
    background: #374151;
    cursor: pointer;
    box-shadow: 0 0 0 1px rgba(0,0,0,0.08);
    margin-top: 0;
  }
  input[type="range"].box-slider-input::-moz-range-track {
    width: 100%;
    height: 100%;
    background: transparent;
  }
  input[type="range"].box-slider-input::-moz-range-thumb {
    width: 4px;
    height: 40px;
    border-radius: 2px;
    background: #374151;
    cursor: pointer;
    border: none;
  }
`;

const BOX_SLIDER_RADIUS = 10;
const BOX_SLIDER_FILL_COLOR = "#3399FF";
const BOX_SLIDER_RIGHT_COLOR = "#e5e7eb";

function BoxSlider({ min, max, step, value, onChange }) {
  const trackRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startValue: 0, trackWidth: 0, pointerId: null });
  const touchActiveRef = useRef(false);
  const pct = max > min ? ((Number(value) - min) / (max - min)) * 100 : 0;
  const range = max - min;

  const onPointerDown = (e) => {
    if (dragRef.current.active) return;
    const el = trackRef.current;
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = el.getBoundingClientRect();
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startValue: Number(value),
      trackWidth: rect.width,
      pointerId: e.pointerId,
    };
    el.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.active || e.pointerId !== dragRef.current.pointerId) return;
    const { startX, startValue, trackWidth } = dragRef.current;
    const deltaX = e.clientX - startX;
    const deltaValue = (deltaX / trackWidth) * range;
    let newVal = startValue + deltaValue;
    if (step > 0) newVal = min + Math.round((newVal - min) / step) * step;
    newVal = Math.max(min, Math.min(max, newVal));
    onChange(newVal);
  };

  const onPointerUp = (e) => {
    if (e.pointerId !== dragRef.current.pointerId) return;
    dragRef.current.active = false;
    trackRef.current?.releasePointerCapture(e.pointerId);
  };

  const onTouchStart = (e) => {
    touchActiveRef.current = true;
    e.stopPropagation();
  };
  const onTouchEnd = () => {
    touchActiveRef.current = false;
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onTouchMove = (e) => {
      if (touchActiveRef.current && e.cancelable) e.preventDefault();
      e.stopPropagation();
    };
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", onTouchMove);
  }, []);

  return (
    <div
      ref={trackRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      style={{
        position: "relative",
        borderRadius: BOX_SLIDER_RADIUS,
        height: 40,
        overflow: "hidden",
        touchAction: "none",
        cursor: "pointer",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          background: `linear-gradient(to right, ${BOX_SLIDER_FILL_COLOR} 0%, ${BOX_SLIDER_FILL_COLOR} ${pct}%, ${BOX_SLIDER_RIGHT_COLOR} ${pct}%, ${BOX_SLIDER_RIGHT_COLOR} 100%)`,
          borderRadius: BOX_SLIDER_RADIUS,
          pointerEvents: "none",
        }}
      />
      <input
        type="range"
        className="box-slider-input"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        tabIndex={-1}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          width: "100%",
          height: "100%",
          margin: 0,
          padding: 0,
          appearance: "none",
          WebkitAppearance: "none",
          background: "transparent",
          outline: "none",
          cursor: "pointer",
          opacity: 1,
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

export default BoxSlider;
